import { z } from 'zod';
import { applicabilitySchema, assertNever, personAttributeSchema, requirementStatusSchema, } from './vocabulary.js';
/**
 * When a requirement applies to a person (ADR-006 §4).
 *
 * A FLAT AND of at most four typed clauses. Not a recursive AST, and emphatically not an
 * expression language:
 *
 *  - An interpreter's input space cannot be enumerated in a test. A flat clause list's
 *    can, which is the same argument that keeps readiness precedence in code.
 *  - A nested AST cannot be rendered as an editable form, so HR could not actually
 *    configure it — which defeats the premise the model exists to serve.
 *  - Expressions reference fields by string, so a rename breaks rules silently, in
 *    production, on one person's record.
 *
 * OR is written as two bindings, which is also what an auditor reads. NOT is a value set:
 * every clause matches against `anyOf`, so "not a driver" is the complement listed out.
 * Where a genuine NOT is needed — the HHA 24-month lapse rule, an *absence* of events —
 * it is not a trigger at all but a new expiry kind. The general rule: when you want NOT,
 * you need a kind.
 */
/** Reads a person attribute. Absent means UNANSWERED, never false. */
const attributeClauseSchema = z.object({
    kind: z.literal('attribute'),
    attribute: personAttributeSchema,
    /** Matches when the person's value is one of these. */
    anyOf: z.array(z.string().min(1)).min(1),
});
/** Reads another requirement's derived status — how prerequisites chain. */
const requirementStatusClauseSchema = z.object({
    kind: z.literal('requirement-status'),
    /** The other requirement's stable key. */
    requirementKey: z.string().min(1),
    anyOf: z.array(requirementStatusSchema).min(1),
});
/**
 * Whether a record of some type exists for the person — an exposure incident, a
 * disciplinary action. Present/absent is always answerable, so this clause never yields
 * `unknown`.
 */
const recordExistsClauseSchema = z.object({
    kind: z.literal('record-exists'),
    recordType: z.string().min(1),
    expected: z.boolean().default(true),
});
export const triggerClauseSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('employer-responsibility'), anyOf: z.array(z.enum(['vigeo', 'other'])).min(1) }),
    z.object({ kind: z.literal('employment-classification'), anyOf: z.array(z.enum(['w2', '1099'])).min(1) }),
    attributeClauseSchema,
    requirementStatusClauseSchema,
    recordExistsClauseSchema,
]);
/**
 * Four is a deliberate ceiling. Past four ANDed conditions a rule has stopped being
 * readable by the person approving it, and the honest move is a separate requirement key
 * rather than a longer conjunction.
 */
export const MAX_TRIGGER_CLAUSES = 4;
export const triggerSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('always') }),
    z.object({
        mode: z.literal('conditional'),
        clauses: z.array(triggerClauseSchema).min(1).max(MAX_TRIGGER_CLAUSES),
        /**
         * Decision 7 as a seeded DEFAULT, not a hard-coded rule — the binding constraint says
         * this stays configurable. False means the row appears only once triggered.
         */
        showBeforeTrigger: z.boolean().default(false),
        /** Rendered on the checklist so HR can see why a row appeared. */
        explanation: z.string().default(''),
    }),
]);
function evaluateClause(clause, facts) {
    switch (clause.kind) {
        case 'employer-responsibility': {
            const employer = facts.employerResponsibility;
            return employer == null ? 'unknown' : clause.anyOf.includes(employer) ? 'applies' : 'does_not_apply';
        }
        case 'employment-classification': {
            const classification = facts.employmentClassification;
            return classification == null ? 'unknown' : clause.anyOf.includes(classification) ? 'applies' : 'does_not_apply';
        }
        case 'attribute': {
            const value = facts.attributes[clause.attribute];
            if (value === undefined)
                return 'unknown';
            return clause.anyOf.includes(value) ? 'applies' : 'does_not_apply';
        }
        case 'requirement-status': {
            const status = facts.requirementStatuses[clause.requirementKey];
            if (status === undefined)
                return 'unknown';
            return clause.anyOf.includes(status) ? 'applies' : 'does_not_apply';
        }
        case 'record-exists': {
            const present = facts.recordTypesPresent.includes(clause.recordType);
            return present === clause.expected ? 'applies' : 'does_not_apply';
        }
        default:
            return assertNever(clause, 'evaluateClause');
    }
}
/**
 * Kleene AND over the clauses, and the ORDER of these two checks is the whole design.
 *
 * A definite `does_not_apply` wins over an `unknown`, because a rule that provably does
 * not apply cannot be made uncertain by an unrelated blank field — otherwise every
 * unanswered attribute would drag unrelated requirements into review. But `unknown` wins
 * over `applies`, because a half-answered conjunction is not a yes.
 *
 * Pure and total: an unresolvable fact yields `unknown`, never a throw.
 */
export function evaluateTrigger(trigger, facts) {
    if (trigger.mode === 'always')
        return 'applies';
    let sawUnknown = false;
    for (const clause of trigger.clauses) {
        const result = evaluateClause(clause, facts);
        if (result === 'does_not_apply')
            return 'does_not_apply';
        if (result === 'unknown')
            sawUnknown = true;
    }
    return sawUnknown ? 'unknown' : 'applies';
}
export { applicabilitySchema };
//# sourceMappingURL=triggers.js.map